import React from 'react';

import cs from 'classnames';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {TooltipList} from 'app/components/general/TooltipList';
import StateButton from 'app/general/widgets/StateButton';
import {useAddReaction, useRemoveReaction} from 'app/reactions/api';
import {useUserReactedMap} from 'app/reactions/hooks';
import {filterSkinToneReactions, modifyReaction} from 'app/reactions/utils';
import {gt} from 'app/utils/i18n';

import type {
  CommentReactionsType,
  CommentReactionTypeWithTone,
  CommentSerializedType,
} from 'sweet-types';

interface Props {
  reactions: CommentReactionsType;
  comment: CommentSerializedType;
}

export const ReactionsCounterButtons = ({
  reactions,
  comment,
}: Props): JSX.Element => {
  const [addReaction] = useAddReaction();
  const [removeReaction] = useRemoveReaction();

  const userReactionsMap = useUserReactedMap(reactions);
  const availableSkinTonedReaction = filterSkinToneReactions(reactions);

  function handleToggleReaction(slug: CommentReactionTypeWithTone) {
    const toggleAction =
      userReactionsMap[slug] === true ? removeReaction : addReaction;

    return toggleAction({
      name: slug,
      object_id: comment.id,
      content_type: 'commentwithmeta',
    });
  }

  return (
    <div className="btn-group flex-wrap">
      {availableSkinTonedReaction.map(
        ({icon, title, slug, modifier, users}) => {
          if (reactions[slug] == null) {
            return null;
          }

          return (
            <LazyTooltip
              key={`${slug}-${users.length}`} // Use a key that includes the reaction count to force remount when reactions change. This helps prevent tooltip misalignment when reactions are toggled quickly
              tooltip={
                <TooltipList
                  title={<span>{gt('Reacted with %s:', title)}</span>}
                  items={users}
                />
              }
            >
              <StateButton
                onClick={() => handleToggleReaction(slug)}
                busyText={
                  <small className="text-dark">{reactions[slug].length}</small>
                }
                className={cs(
                  'btn btn-sm btn-outline-light px-1 mb-1 py-0 d-flex flex-wrap flex-grow-0 align-items-center justify-content-center',
                  {active: userReactionsMap[slug]}
                )}
              >
                <span>{modifyReaction(icon, modifier.tone)}</span>{' '}
                <small className="mx-1 text-dark">{users.length}</small>
              </StateButton>
            </LazyTooltip>
          );
        }
      )}
    </div>
  );
};
